// Speeds and data as the admin screens show them (Bandwidth Manager, the dashboard card,
// Internet Plans, Vouchers). Speeds are kbps, as plans keep them (1 Mbps = 1024 kbps, as
// the plan form enters them); a plan's data limit is MB.

export const KBPS_PER_MBPS = 1024;
export const MAX_MBPS = 50;
export const MIN_KBPS = 64;

export function speedText(kbps) {
    const n = Number(kbps) || 0;
    if (!n) return '—';
    if (n < KBPS_PER_MBPS) return `${Math.round(n)} kbps`;
    const mbps = n / KBPS_PER_MBPS;
    return `${mbps >= 10 ? Math.round(mbps) : Math.round(mbps * 10) / 10} Mbps`;
}

export function bytesText(b) {
    const n = Number(b) || 0;
    if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
    if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
}

/** A plan's data limit as the admin entered it: "5 GB", "500 MB" — "Unlimited" for none. */
export function dataLimitText(dataLimit) {
    const mb = Number(dataLimit?.total) || 0;
    if (!mb) return 'Unlimited';
    if (dataLimit.unit === 'GB') return `${Math.round((mb / 1024) * 100) / 100} GB`;
    return `${mb} MB`;
}

/** A plan's speed one way: "2 Mbps – 20 Mbps" with a guaranteed minimum, else "20 Mbps". */
export function speedRangeText(max, min) {
    if (!max) return 'Gateway default';
    return min ? `${speedText(min)} – ${speedText(max)}` : speedText(max);
}

/** A plan's validity: "1 day", "2 hours", "30 minutes". */
export function validityText(validity) {
    if (!validity?.duration) return '—';
    const unit = validity.unit || 'days';
    return `${validity.duration} ${Number(validity.duration) === 1 ? unit.replace(/s$/, '') : unit}`;
}

/** What a plan gives, in a line: "2 hours · 5 GB · 20 Mbps ↓ / 5 Mbps ↑". */
export function planSummary(plan) {
    if (!plan) return '';
    const v = plan.validity;
    const bw = plan.bandwidth || {};
    return [
        v?.duration && validityText(v),
        plan.dataLimit ? dataLimitText(plan.dataLimit) : null,
        bw.downloadSpeed ? `${speedText(bw.downloadSpeed)} ↓ / ${speedText(bw.uploadSpeed)} ↑` : null,
    ].filter(Boolean).join(' · ');
}

export const toKbps = (mbps) => Math.round(Number(mbps) * KBPS_PER_MBPS);
export const toMbps = (kbps) => Math.round((Number(kbps) / KBPS_PER_MBPS) * 100) / 100;

// Who gave a guest the speed they have now.
export const SOURCE_LABELS = { plan: 'Plan', dynamic: 'Dynamic', admin: 'Set by admin' };
// …and in the change log, who changed it.
export const CHANGE_LABELS = { admin: 'Admin', dynamic: 'Dynamic Bandwidth Manager', reset: 'Back to plan' };
// The gateway's answer to the change (CoA).
export const GATEWAY_LABELS = { ack: 'Applied live', nak: 'Refused by the gateway', 'no-reply': 'Gateway did not answer', 'not-sent': 'Not sent' };

/** How full a link is, 0–100 (null when its speed is not set). */
export function percentOf(usedKbps, capacityKbps) {
    if (!capacityKbps) return null;
    return Math.min(100, Math.round(((Number(usedKbps) || 0) / capacityKbps) * 100));
}
